/**
 * 이미지 편집 OCR corners sessionStorage 캐시.
 * 레거시 `buildOcrCoordinateStorageKey` / useOcrDrugWorkflow 과 동일 키·스키마.
 */
import { normalizeCornerPoints } from '@/shared/utils/ocrCropWarpUtils';
import type { Point2D } from '@/shared/utils/ocrImagePreprocess';

export const OCR_COORDINATE_STORAGE_PREFIX = {
  prescriptionReview: 'prescription-review-ocr-coordinates',
  maxMatchMini: 'max-match-mini-ocr-coordinates',
  maxMatchOps: 'max-match-ops-ocr-coordinates',
} as const;

export type OcrCoordinateStoragePrefix =
  (typeof OCR_COORDINATE_STORAGE_PREFIX)[keyof typeof OCR_COORDINATE_STORAGE_PREFIX];

export interface OcrCoordinateBbox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface StoredOcrCoordinates {
  sourceImageUrl: string;
  points: Point2D[];
  bbox?: OcrCoordinateBbox | null;
}

export const buildOcrCoordinateStorageKey = (
  code: string | null | undefined,
  prefix: OcrCoordinateStoragePrefix | string,
): string | null => (code ? `${prefix}:${code}` : null);

export const loadOcrCoordinatesFromSession = (
  storageKey: string | null,
  sourceImageUrl: string | null | undefined,
): StoredOcrCoordinates | null => {
  if (!storageKey || !sourceImageUrl || typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredOcrCoordinates;
    if (stored?.sourceImageUrl !== sourceImageUrl) return null;
    const points = normalizeCornerPoints(stored?.points);
    if (points.length !== 4) return null;
    return {
      sourceImageUrl: stored.sourceImageUrl,
      points,
      bbox: stored.bbox ?? null,
    };
  } catch {
    return null;
  }
};

export const saveOcrCoordinatesToSession = (
  storageKey: string | null,
  payload: StoredOcrCoordinates,
): void => {
  if (!storageKey || typeof window === 'undefined') return;
  const points = normalizeCornerPoints(payload.points);
  if (points.length !== 4 || !payload.sourceImageUrl) return;
  try {
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        sourceImageUrl: payload.sourceImageUrl,
        points,
        bbox: payload.bbox ?? null,
      }),
    );
  } catch {
    // quota / private mode — 무시
  }
};
